import { z } from 'zod'
import { Bus, DuckingRule } from './audio.js'
import { Emitter } from './emitters.js'
import { Course, Gate, Region } from './geometry.js'
import { GainDb, Id, Metres } from './primitives.js'
import { Cue, Rule, VariableDeclaration } from './rules.js'

/**
 * An area where everything ducks — a road crossing on a running route. Worlds
 * that send people into traffic must be able to get out of their ears.
 */
export const SafetyZone = z.object({
  id: Id,
  name: z.string().max(120),
  regionId: Id,
  duckDb: z.number().finite().min(0).max(60).default(18),
})
export type SafetyZone = z.infer<typeof SafetyZone>

export const Anchoring = z.enum(['geo', 'portable'])
export const Instancing = z.enum(['personal', 'shared'])

/**
 * The complete authored world: geometry, sound, and cause and effect. Versioned
 * as a single document so a published version is immutable and a running
 * session can pin exactly what it started with.
 */
export const WorldDefinition = z.object({
  schemaVersion: z.literal(1),
  id: Id,
  name: z.string().max(120),
  anchoring: Anchoring,
  instancing: Instancing,

  buses: z.array(Bus).min(1),
  ducking: z.array(DuckingRule).default([]),

  regions: z.array(Region).default([]),
  gates: z.array(Gate).default([]),
  courses: z.array(Course).default([]),

  emitters: z.array(Emitter).default([]),
  rules: z.array(Rule).default([]),
  cues: z.array(Cue).default([]),
  variables: z.array(VariableDeclaration).default([]),
  safetyZones: z.array(SafetyZone).default([]),

  /** Above this horizontal accuracy, spatial triggers hold rather than misfire. */
  accuracyFloorM: Metres.default(25),
  /** Ceiling applied after the mix, before the limiter. */
  masterGainDb: GainDb.default(0),
})
export type WorldDefinition = z.infer<typeof WorldDefinition>

/** The buses every world starts with, matching the default ducking matrix. */
export function defaultBuses(): Bus[] {
  return [
    { id: 'narration', name: 'Narration', gainDb: 0 },
    { id: 'ambience', name: 'Ambience', gainDb: 0 },
    { id: 'music', name: 'Music', gainDb: 0 },
    { id: 'sfx', name: 'SFX', gainDb: 0 },
    { id: 'live', name: 'Live', gainDb: 0 },
    { id: 'ui', name: 'UI', gainDb: 0 },
  ]
}

/**
 * Narration and live voice pull ambience and music down. Without this the
 * moment someone starts talking is the moment they become unintelligible.
 */
export function defaultDucking(): DuckingRule[] {
  const rules: DuckingRule[] = []
  for (const sourceBusId of ['narration', 'live']) {
    for (const targetBusId of ['ambience', 'music']) {
      rules.push({ sourceBusId, targetBusId, amountDb: 9, attackMs: 150, releaseMs: 600 })
    }
  }
  return rules
}
